import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {createGeometryValueData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryValueNode} from "#template/nodes/geometry-graph/value/value-node"
import {pointGeometryAttributeLike, curveGeometryAttributeLike, meshGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {
    PointGeometryAttributeLikeNode,
    CurveGeometryAttributeLikeNode,
    MeshGeometryAttributeLikeNode,
} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {ReduceOperatorOption} from "#template/nodes/geometry-graph/options"
import {reduceOperatorOption} from "#template/nodes/geometry-graph/options"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type ReduceOperatorParameters = {
    name: string
    attribute: PointGeometryAttributeLikeNode | CurveGeometryAttributeLikeNode | MeshGeometryAttributeLikeNode | null
    operator: ReduceOperatorOption
}

export const geometryReduceOperatorParametersSchema = z.object({
    name: z.string(),
    attribute: z.nullable(z.union([pointGeometryAttributeLike, curveGeometryAttributeLike, meshGeometryAttributeLike])),
    operator: reduceOperatorOption,
})

function applyReduceOperator<T extends IndexingDim>(arg: GeometryAttributeData<T>, option: ReduceOperatorOption): GeometryValueData {
    const argRefData = arg.ref

    const reducedAttrRefData = applyGeometryOperator((argAttrRef: AttributeRef) => {
        switch (option) {
            case "min":
                return argAttrRef.reduceMin()
            case "max":
                return argAttrRef.reduceMax()
            default:
                throw new Error(`Unknown reduce operator option: ${option}`)
        }
    }, argRefData)

    return createGeometryValueData(reducedAttrRefData)
}

export abstract class GeometryReduceOperatorNode extends GeometryValueNode<ReduceOperatorParameters> {
    protected override hasValidParameters(
        parameters: ReduceOperatorParameters,
    ): parameters is ReduceOperatorParameters & NonNullableParameters<ReduceOperatorParameters> {
        return parameters.attribute !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<ReduceOperatorParameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryValueData | null>; invalidator?: BuilderInlet<null>} {
        const {attribute, operator: operatorName} = parameters

        const [evaluatedAttributeValid, evaluatedAttributeInvalid] = scope.branch(evaluator.evaluate(attribute))

        const geometryValueData = scope.pureLambda(
            scope.tuple(evaluatedAttributeValid, operatorName),
            ([attributeData, operatorName]) => applyReduceOperator(attributeData, operatorName),
            "applyReduceOperator",
        )

        return {
            evaluatedGeometryData: geometryValueData,
            invalidator: evaluatedAttributeInvalid,
        }
    }
}
